import { queryClient } from "@shared/api/queries/queryClient";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Loader2, MessageSquare, Send } from "lucide-react";
import { useEffect, useState, useMemo, useRef } from "react";
import { toast } from "@shared/hooks/use-toast";
import { CallEntryData, InlineCallEntry } from "./addCallEntryDialogue";
import { useTemplates } from "@features/ux/messaging/hooks";
import { mapToLabelValue } from "@shared/utils/mapToLabelValue";

interface CommunicationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  consumerId: string | undefined;
  email: string | undefined;
  phone: string | undefined;
  remoteUtilityId: number;
  communicationType: string;
  useChannelType: (remoteUtilityId: number) => any;
  useChannelTemplates: (channelType: string, remoteUtilityId: number) => any;
  useSendCommunication: (consumerId: string) => any;
}

export function CommunicationDialog({
  isOpen,
  onClose,
  consumerId,
  remoteUtilityId,
  email,
  phone,
  useChannelType,
  useChannelTemplates,
  useSendCommunication,
  communicationType,
}: CommunicationDialogProps) {
  const hardcodedChannelTypes = [
    {
      key: "CALL",
      value: "Call",
    },
    {
      key: "EMAIL",
      value: "Email",
    },
    {
      key: "TEXT",
      value: "Text",
    },
    {
      key: "WHATSAPP",
      value: "Whatsapp",
    },
    {
      key: "NOTIFICATION",
      value: "Notification",
    },
  ];

  const [formData, setFormData] = useState({
    channel: "",
    template: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [callEntryData, setCallEntryData] = useState<CallEntryData | null>(
    null
  );
  const [isCallFormValid, setIsCallFormValid] = useState(false);
  const inlineCallEntryRef = useRef<{
    submitCallEntry: () => Promise<CallEntryData>;
  } | null>(null);

  // Get channel types from hook
  const channelTypes = hardcodedChannelTypes;
  const channelsLoading = false;
  const channelsError = null;

  const utilityId = remoteUtilityId.toString();
  // Use the send communication hook
  const sendCommunication = useSendCommunication(consumerId || "");
  const { data: templates } = useTemplates({
    channelType: formData.channel,
    remoteUtilityId: utilityId,
  });

  const templateList = mapToLabelValue(templates, "templateName", "id");

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      const resetData = {
        channel: "",
        template: "",
      };
      setFormData(resetData);
      setCallEntryData(null);
      setIsCallFormValid(false);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  // Reset template when channel changes
  useEffect(() => {
    if (formData.channel) {
      setFormData((prev) => ({
        ...prev,
        template: "",
      }));
      setCallEntryData(null);
      setIsCallFormValid(false);
    }
  }, [formData.channel]);

  // Generate channel options
  const getChannelOptions = useMemo(() => {
    if (!channelTypes || !Array.isArray(channelTypes)) {
      return [];
    }
    return channelTypes
      .filter(
        (item) => item && typeof item === "object" && item.key && item.value
      )
      .map((channelType) => ({
        value: channelType.key,
        label: channelType.value,
      }));
  }, [channelTypes]);

  // Form fields configuration
  const formFields: FormField[] = [
    {
      name: "channel",
      type: "select",
      label: "Communication Channel",
      placeholder: channelsLoading
        ? "Loading channels..."
        : channelsError
        ? "Error loading channels"
        : "Select channel",
      required: true,
      options: getChannelOptions,
      disabled: channelsLoading || !!channelsError,
      className: "col-span-2 mt-3",
    },
    // Custom field for Call Entry - Inline
    {
      name: "callEntry",
      type: "text", // This will be overridden by customRender
      label: "",
      required: false,
      className: "col-span-2 mt-3",
      showWhen: (formValues) => formValues.channel === "CALL",
      customRender: (field, form, error) => {
        return (
          <div>
            <InlineCallEntry
              ref={inlineCallEntryRef}
              entityId={consumerId || ""}
              remoteUtilityId={remoteUtilityId || 0}
              action={communicationType}
              onCallDataChange={(callData: CallEntryData) => {
                setCallEntryData(callData);
              }}
              onValidationChange={(isValid: boolean) => {
                setIsCallFormValid(isValid);
              }}
              onCancel={() => {
                // Reset channel selection if user cancels
                setFormData((prev) => ({
                  ...prev,
                  channel: "",
                }));
                setCallEntryData(null);
                setIsCallFormValid(false);
              }}
              showSubmitButton={false} // Hide submit button in inline component
            />
          </div>
        );
      },
    },
    {
      name: "template",
      type: "select",
      label: "Select Template",
      required: false,
      placeholder: "Select a Template",
      options: templateList,
      className: "col-span-2",
      showWhen: (formValues) => formValues.channel != "CALL",
    },
  ];

  // Create form using FormService
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: formData,
  });

  // Sync form with formData
  useEffect(() => {
    form.reset(formData);
  }, [formData, form]);

  // Handle field changes
  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!formData.channel || !consumerId) return;

    setIsSubmitting(true);

    try {
      // Handle CALL channel
      if (formData.channel === "CALL") {
        if (!inlineCallEntryRef.current) {
          throw new Error("Call entry component not ready");
        }

        // Submit the call entry through the inline component
        await inlineCallEntryRef.current.submitCallEntry();

        toast({
          title: "Call entry completed successfully!",
        });
        handleClose();
        return;
      }
      // Prepare the communication data
      const communicationData = {
        remoteUtilityId: Number(remoteUtilityId),
        channelType: formData.channel,
        template_id: formData.template,
        to: formData.channel === "EMAIL" ? [email || ""] : [],
        context: {
          entityId: Number(consumerId),
          actionType: communicationType,
        },
      };

      // Send the communication using the hook
      await sendCommunication.mutateAsync(communicationData);
      await queryClient.refetchQueries({
        queryKey: ["cx","communication","communicationHistory",consumerId],
        exact: false,
      });
      toast({
        title: "Communication sent successfully!",
      });
      handleClose();
    } catch (error: any) {
      toast({
        title: `Failed to send communication: ${
          error.message || "Unknown error"
        }`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle form reset/close
  const handleReset = () => {
    const resetData = {
      channel: "",
      template: "",
    };
    setFormData(resetData);
    setCallEntryData(null);
    setIsCallFormValid(false);
    form.reset(resetData);
  };

  // Handle dialog close
  const handleClose = () => {
    if (!isSubmitting) {
      handleReset();
      onClose();
    }
  };

  // Format error message safely
  const getErrorMessage = (error: any) => {
    if (!error) return null;
    return error.message || error.toString() || "An error occurred";
  };

  // Check if form is valid for submission
  const isFormValid = () => {
    if (!formData.channel) return false;

    if (formData.channel === "CALL") {
      return isCallFormValid;
    }

    return formData.template !== "";
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl h-[calc(100vh-5rem)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Send Communication
          </DialogTitle>
          <DialogDescription>
            Send a communication to the selected consumer using available
            channels and templates.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Consumer Information */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 mb-2"></div>
            <p className="text-sm text-gray-600">
              Communication will be sent to the selected consumer
            </p>
          </div>

          {/* Error Display */}
          {channelsError && (
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <div className="text-sm text-red-600 space-y-1">
                {channelsError && (
                  <div>Channel Error: {getErrorMessage(channelsError)}</div>
                )}
              </div>
            </div>
          )}

          {/* Dynamic Form */}
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            onReset={handleClose}
            onFieldChange={handleFieldChange}
            gridColumns={2}
            responsiveColumns={{
              sm: 1,
              md: 2,
            }}
            config={{
              showResetButton: true,
              showSubmitButton: true,
              submitButtonText: isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {formData.channel === "CALL" ? "Processing..." : "Sending..."}
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  {formData.channel === "CALL" ? "Complete Call" : "Send"}
                </>
              ),
              resetButtonText: "Cancel",
              spacing: "comfortable",
              classes: {
                form: "space-y-6",
                button: "px-4 py-2 rounded-md font-medium transition-colors",
                buttonContainer:
                  "flex justify-end gap-3 pt-6 border-gray-200 mt-8",
                input:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                textarea:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
                select:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
                label: "block text-sm font-medium text-gray-700 mb-1",
                error: "text-sm text-red-600 mt-1",
                container: "space-y-1",
              },
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
